import React, { useEffect, useState } from "react"
import { createFFmpeg, fetchFile } from "@ffmpeg/ffmpeg"
import { Button, Container, Form, Row } from "react-bootstrap"
import mime from "mime-types"
import FileSaver from "file-saver"
import ConsoleOutput from "../../components/ConsoleOutput/ConsoleOutput"

const ffmpeg = createFFmpeg({ log: true })

function Ffmpeg() {
  const [isFfmpegLoaded, setIsFfmpegLoaded] = useState(false)
  const [ffmpegCommands, setFfmpegCommands] = useState<string>()
  const [outputBlob, setOutputBlob] = useState<Blob>()
  const [outputFileName, setOutputFileName] = useState<string>()
  const [consoleOutputText, setConsoleOutputText] = useState<string[]>([])
  const [filesInMem, setFilesInMem] = useState<string[]>([])

  useEffect(() => {
    loadFfmpeg()
    initConsoleOutput()
  }, [])

  function initConsoleOutput() {
    const log = console.log.bind(console)
    console.log = (...args: any[]) => {
      const consoleOutput = args.toString()
      setConsoleOutputText((prev) => [...prev, consoleOutput])
      log(...args)
    }
  }

  async function loadFfmpeg() {
    await ffmpeg.load()
    setIsFfmpegLoaded(true)
  }

  async function onFileSet(files: FileList | null) {
    if (!files) return
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i)
      if (file) {
        ffmpeg.FS("writeFile", file.name, await fetchFile(file)) //the await does actually have an effect...
        setFilesInMem((cur) => [...cur, file.name])
      }
    }
  }

  //sample command: -i big_buck_bunny_720p_5mb.mp4 -t 2.5 -ss 2.0 -f gif output.gif
  //ffmpeg -i big_buck_bunny_720p_5mb.mp4 -i Forum.jpg -filter_complex "[0:v][1:v]overlay=25:25:enable='between(t,0,20)'"  -pix_fmt yuv420p -c:a copy output.mp4
  //ffmpeg -i big_buck_bunny_720p_5mb.mp4 -framerate 30000/1001 -loop 1 -i Forum.jpg -filter_complex "[1:v] fade=out:st=30:d=1:alpha=1 [ov]; [0:v][ov] overlay=10:10 [v]" -map "[v]" -map 0:a -c:v libx264 -c:a copy -shortest out.mp4

  async function runFfmpegCommands() {
    // if (!file) return //can run ffmpeg without input file!
    console.log("Running ffmpeg with command: ", ffmpegCommands)
    let finalCommands = ffmpegCommands?.replace("ffmpeg", "")
    try {
      if (finalCommands) {
        console.log("Shimmed command: ", finalCommands)
        const finalCommandsSplit = finalCommands.split(" ")
        await ffmpeg.run(...finalCommandsSplit)
        const outputFileName = finalCommandsSplit[finalCommandsSplit.length - 1]
        const data = ffmpeg.FS("readFile", outputFileName)
        const mimeType = mime.lookup(outputFileName)
        const blob = new Blob(
          [data.buffer],
          mimeType !== false ? { type: mimeType } : {}
        )
        setOutputFileName(outputFileName)
        setOutputBlob(blob)
      }
    } catch (error) {
      console.error(
        `Error running ffmpeg with final commands ${finalCommands}: ${error}`
      )
    }
  }

  return (
    <div className="App">
      {isFfmpegLoaded ? (
        <Container className="my-5">
          <Form
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault()
                runFfmpegCommands()
              }
            }}
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              runFfmpegCommands()
            }}
          >
            <Row>
              <Form.File
                className="text-center"
                style={{ margin: "auto", width: "150px" }}
                label="Add Input Files"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onFileSet(e.target.files)
                }
                multiple
              />
            </Row>
            {filesInMem.length > 0 && (
              <Row
                className="mt-5"
                style={{ alignContent: "center", justifyContent: "center" }}
              >
                <Container>
                  <Row
                    style={{ alignContent: "center", justifyContent: "center" }}
                  >
                    <h5>Files in memory:</h5>
                  </Row>
                  <Row
                    className="text-left"
                    style={{ alignContent: "center", justifyContent: "center" }}
                  >
                    <ul>
                      {filesInMem.map((f) => (
                        <li key={f}>
                          <text>{f}</text>
                        </li>
                      ))}
                    </ul>
                  </Row>
                </Container>
              </Row>
            )}

            <Row
              className="mt-5"
              style={{ alignContent: "center", justifyContent: "center" }}
            >
              <Form.Group controlId="ffmpeg-commands-input">
                <Form.Label>FFmpeg Command</Form.Label>
                <Form.Control
                  style={{ width: "70vw", backgroundColor: "transparent" }}
                  type="text"
                  placeholder="Enter FFmpeg command e.g. -i myinput.mp4 -t 2.5 -ss 2.0 -f gif myoutput.gif"
                  onChange={(e) => setFfmpegCommands(e.target.value)}
                />
              </Form.Group>
            </Row>
            <Row
              className="my-2"
              style={{ alignContent: "center", justifyContent: "center" }}
            >
              <Button variant="dark" type="submit" className="mx-3 mb-3">
                Run FFmpeg
              </Button>
              {
                <Button
                  className="mx-3 mb-3"
                  disabled={!outputBlob}
                  variant="success"
                  onClick={() =>
                    outputBlob && FileSaver.saveAs(outputBlob, outputFileName)
                  }
                >
                  Download Output File
                </Button>
              }
            </Row>
          </Form>
          <Row style={{ alignContent: "center", justifyContent: "center" }}>
            <ConsoleOutput
              text={consoleOutputText.reduce(
                (acc, next) => `${acc}\n\n${next}`,
                ""
              )}
            ></ConsoleOutput>
          </Row>
        </Container>
      ) : (
        <div>Loading...</div>
      )}
    </div>
  )
}

export default Ffmpeg
